import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PrintModal.css';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ScaleLine from 'ol/control/ScaleLine';
import northArrow from '../../../../_assets/img/north.png';
import { Button } from 'primereact/button';

const PREVIEW_PLACEHOLDER =
  'data:image/svg+xml;utf8,' + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
  <rect width="100%" height="100%" fill="#ffffff"/>
  <g fill="#000000">
    <rect x="350" y="200" rx="80" ry="80" width="500" height="400"/>
    <rect x="450" y="260" width="500" height="400" rx="80" ry="80"/>
    <path d="M520 560 L630 420 L690 480 L760 380 L880 560 Z" fill="#ffffff"/>
    <circle cx="820" cy="340" r="30" fill="#ffffff"/>
  </g>
</svg>`);

// ----- Tamanhos de papel (mm)
const PAPER_MM = {
  'A4-P': { w: 210, h: 297, label: 'A4 Retrato' },
  'A4-L': { w: 297, h: 210, label: 'A4 Paisagem' },
  'A3-P': { w: 297, h: 420, label: 'A3 Retrato' },
  'A3-L': { w: 420, h: 297, label: 'A3 Paisagem' },
};

// ----- Configurações de template (exemplo)
const CONFIGS = {
  default: {
    label: 'Padrão',
    marginsMm: { top: 15, right: 15, bottom: 18, left: 15 },
    showWatermark: true,
    watermarkSrc: '/portal/logo-pedea-wm.png',
  },
  idesp: {
    label: 'idesp',
    marginsMm: { top: 12, right: 12, bottom: 20, left: 12 },
    showWatermark: true,
    watermarkSrc: '/portal/logo-pedea-wm.png',
  },
};

// util: mm -> inches -> px
const mmToPx = (mm, dpi) => Math.round((mm / 25.4) * dpi);

function getActiveLayers(map) {
  const layers = map.getLayers().getArray();

  return layers
    .filter(layer => layer.getVisible())
    .map(l => ({
      title: l.get('title'),
      name: l.get('name'),
      wmsName: l.get('wmsName'),
      serverType: l.get('serverType')
    }))
    .filter(l => typeof l.title === 'string' && l.title.trim() !== '')
    .filter(l => l.title.toLowerCase() !== 'csv')
    .filter(l =>
      l.serverType !== 'esri' &&
      l.title.toLowerCase() !== 'mapa topográfico')
    .map(l => l.title.trim())
    .sort((a, b) => a.localeCompare(b))
    .join(', ');
}

function drawScale(ctx, map, paperHpx, marginPx) {
  const scaleLine = new ScaleLine();
  scaleLine.setMap(map);
  const el = scaleLine.element;
  el.style.position = 'absolute';
  el.style.top = '-1000px';
  document.body.appendChild(el);

  map.renderSync();
  const txt = el.innerText;
  document.body.removeChild(el);

  const scaleText = `Escala: ${txt}`;
  ctx.font = 'bold 20px Arial';
  ctx.fillStyle = '#000';
  ctx.fillText(scaleText, marginPx, paperHpx - marginPx);
}

function drawNorth(ctx, paperWpx, topPx) {
  const img = new Image();
  img.src = northArrow;
  return new Promise((resolve) => {
    img.onload = () => {
      const size = 28;
      ctx.drawImage(img, paperWpx - size - topPx, topPx, size, size);
      resolve();
    };
    img.onerror = () => resolve();
  });
}

function drawWatermark(ctx, src, x, y, w) {
  const img = new Image();
  img.src = src;
  return new Promise((resolve) => {
    img.onload = () => {
      const ratio = img.height / img.width;
      ctx.drawImage(img, x, y, w, w * ratio);
      resolve();
    };
    img.onerror = () => resolve();
  });
}

export default function PrintModal({ active, callback, map }) {
  const { t } = useTranslation();

  const [previewSrc, setPreviewSrc] = useState(PREVIEW_PLACEHOLDER);


  const [show, setShow] = useState(false);
  const [dpi, setDpi] = useState(254); // 96 / 150 / 254 / 300
  const [config, setConfig] = useState('idesp');
  const [paper, setPaper] = useState('A4-L');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [author, setAuthor] = useState('');
  const [withLegend, setWithLegend] = useState(false);

  const previewRef = useRef(null); // <img> do preview
  const working = useRef(false);

  useEffect(() => { if (active) setShow(true); }, [active]);
  useEffect(() => { callback(show); }, [show, callback]);

  const paperPx = useMemo(() => {
    const p = PAPER_MM[paper];
    return {
      w: mmToPx(p.w, dpi),
      h: mmToPx(p.h, dpi)
    };
  }, [paper, dpi]);

  const marginsPx = useMemo(() => {
    const m = CONFIGS[config]?.marginsMm || CONFIGS.default.marginsMm;
    return {
      top: mmToPx(m.top, dpi),
      right: mmToPx(m.right, dpi),
      bottom: mmToPx(m.bottom, dpi),
      left: mmToPx(m.left, dpi),
    };
  }, [config, dpi]);

  const mapBox = useMemo(() => {
    // área útil para o mapa (abaixo do título e acima do rodapé)
    const titleBlock = mmToPx(20, dpi);   // reserva para título/subtítulo
    const footerBlock = mmToPx(22, dpi);  // reserva rodapé/escala
    return {
      x: marginsPx.left,
      y: marginsPx.top + titleBlock,
      w: paperPx.w - marginsPx.left - marginsPx.right,
      h: paperPx.h - marginsPx.top - marginsPx.bottom - titleBlock - footerBlock,
    };
  }, [paperPx, marginsPx, dpi]);

  // helper: aguarda o mapa ter renderer, target e size válidos
  function waitMapReady(map) {
    return new Promise((resolve) => {
      // já está pronto?
      const hasRenderer = !!map.getRenderer?.();
      const size = map.getSize?.() || [0, 0];
      const hasTarget = !!map.getTargetElement?.();

      if (hasRenderer && hasTarget && size[0] > 0 && size[1] > 0) {
        // garante um frame sincronizado
        map.renderSync();
        resolve();
        return;
      }

      // quando terminar o primeiro render completo, seguimos
      const done = () => {
        map.un('rendercomplete', done);
        resolve();
      };

      map.once('rendercomplete', done);

      // força um ciclo de render; updateSize ajuda quando o container acabou de abrir (modal)
      try { map.updateSize?.(); } catch (_) { }
      map.render(); // dispara render assíncrono
    });
  }


  async function makeImage() {
    if (!map || working.current) return;
    working.current = true;

    // força render do OL
    await waitMapReady(map);

    map.renderSync();

    // 1) capturar canvas do OL (tela)
    const screenCanvas = document.createElement('canvas');
    const size = map.getSize();
    screenCanvas.width = size[0];
    screenCanvas.height = size[1];
    const sctx = screenCanvas.getContext('2d');

    Array.prototype.forEach.call(document.querySelectorAll('.ol-layer canvas'), (c) => {
      if (c.width > 0) {
        const opacity = c.parentNode.style.opacity;
        sctx.globalAlpha = opacity === '' ? 1 : Number(opacity);
        const transform = c.style.transform;
        const matrix = transform.match(/^matrix\(([^\(]*)\)$/)?.[1]?.split(',').map(Number);
        if (matrix) CanvasRenderingContext2D.prototype.setTransform.apply(sctx, matrix);
        sctx.drawImage(c, 0, 0);
      }
    });

    // 2) montar papel
    const out = document.createElement('canvas');
    out.width = paperPx.w;
    out.height = paperPx.h;
    const ctx = out.getContext('2d');

    // fundo branco
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, out.width, out.height);

    // 3) Título / Subtítulo / Autor
    ctx.fillStyle = '#000';
    ctx.font = 'bold 20px Arial';
    ctx.fillText(title || '', marginsPx.left, marginsPx.top + mmToPx(6, dpi));
    ctx.font = '14px Arial';
    if (subtitle) ctx.fillText(subtitle, marginsPx.left, marginsPx.top + mmToPx(12, dpi));
    if (author) ctx.fillText(author, marginsPx.left, marginsPx.top + mmToPx(18, dpi));

    // 4) mapa (escala para caber na box)
    const scale = Math.min(mapBox.w / screenCanvas.width, mapBox.h / screenCanvas.height);
    const mapDrawW = Math.round(screenCanvas.width * scale);
    const mapDrawH = Math.round(screenCanvas.height * scale);
    const mapDrawX = mapBox.x + (mapBox.w - mapDrawW) / 2;
    const mapDrawY = mapBox.y + (mapBox.h - mapDrawH) / 2;

    ctx.drawImage(screenCanvas, mapDrawX, mapDrawY, mapDrawW, mapDrawH);

    // 5) watermark (se houver no template)
    const conf = CONFIGS[config] || CONFIGS.default;
    if (conf.showWatermark && conf.watermarkSrc) {
      await drawWatermark(ctx, conf.watermarkSrc, marginsPx.left, marginsPx.top - mmToPx(8, dpi), mmToPx(45, dpi));
    }

    // 6) seta do norte e escala
    await drawNorth(ctx, out.width, mmToPx(6, dpi));
    drawScale(ctx, map, out.height, marginsPx.left);

    // 7) rodapé (fonte + camadas ativas) e legenda opcional
    const footerY = out.height - marginsPx.bottom + mmToPx(4, dpi);
    const layersTxt = getActiveLayers(map);

    ctx.font = 'bold 20px Arial';
    ctx.fillStyle = '#000';
    const fonte = `Fonte: PEDEA CE/SEMA`;
    ctx.fillText(`${fonte} | ${layersTxt}`, marginsPx.left, footerY);

    if (withLegend) {
      // Box simples de legenda (lista de camadas). Você pode trocar por GetLegendGraphic depois.
      const legendW = mmToPx(60, dpi);
      const legendH = mmToPx(50, dpi);
      const lx = out.width - marginsPx.right - legendW;
      const ly = footerY - legendH - mmToPx(6, dpi);

      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.strokeStyle = '#999';
      ctx.lineWidth = 1;
      ctx.fillRect(lx, ly, legendW, legendH);
      ctx.strokeRect(lx, ly, legendW, legendH);

      ctx.fillStyle = '#000';
      ctx.font = 'bold 14px Arial';
      ctx.fillText('Legenda', lx + mmToPx(4, dpi), ly + mmToPx(8, dpi));

      ctx.font = '14px Arial';
      const lines = (layersTxt || '').split(', ').slice(0, 6); // até 6 linhas
      lines.forEach((L, i) => {
        ctx.fillText(`• ${L}`, lx + mmToPx(4, dpi), ly + mmToPx(14 + i * 5, dpi));
      });
    }

    // 8) preview
    const dataUrl = out.toDataURL('image/png');
    setPreviewSrc(dataUrl);

    if (previewRef.current) previewRef.current.src = dataUrl;

    working.current = false;
    return dataUrl;
  }

  async function handlePrint() {
    const dataUrl = await makeImage();
    if (!dataUrl) return;

    // download direto
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `mapa_${paper}_${dpi}dpi.png`;
    a.click();
  }

  // gera preview sempre que alterar algo importante
  useEffect(() => { makeImage(); /* eslint-disable-next-line */ }, [dpi, config, paper, title, subtitle, author, withLegend]);

  return (
    <Modal className="PrintModal" size="xl" backdrop="static" keyboard={false} show={show} onHide={() => setShow(false)}>
      <div className="modal-header border-0">
        <h5 className="modal-title">{t('print.title') || 'Imprimir'}</h5>
        <button type="button" className="btn-close" aria-label="Close" onClick={() => setShow(false)} />
      </div>

      <Modal.Body>
        <div className="ContainerPrintWindow">
          {/* Coluna esquerda: preview */}
          <div className="col-12 col-lg-6">
            <img
              alt="preview"
              src={previewSrc}
              className="img-fluid border rounded w-100 preview-img"
              onError={(e) => { e.currentTarget.src = PREVIEW_PLACEHOLDER; }}
            />
          </div>

          {/* Coluna direita: form */}
          <div className="col-12 col-lg-6">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Resolução</label>
                <select className="form-select" value={dpi} onChange={(e) => setDpi(parseInt(e.target.value, 10))}>
                  {[96, 150, 254, 300].map((d) => <option key={d} value={d}>{d} dpi</option>)}
                </select>
              </div>

              {/* <div className="col-6">
                <label className="form-label">Configuração</label>
                <select className="form-select" value={config} onChange={(e) => setConfig(e.target.value)}>
                  {Object.entries(CONFIGS).map(([k, c]) => (
                    <option key={k} value={k}>{c.label}</option>
                  ))}
                </select>
              </div> */}

              <div className="col-12">
                <label className="form-label">Tamanho da folha</label>
                <select className="form-select" value={paper} onChange={(e) => setPaper(e.target.value)}>
                  {Object.entries(PAPER_MM).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <label className="form-label">Título</label>
                <input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>

              <div className="col-12">
                <label className="form-label">Subtítulo</label>
                <input className="form-control" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
              </div>

              <div className="col-12">
                <label className="form-label">Autor</label>
                <input className="form-control" value={author} onChange={(e) => setAuthor(e.target.value)} />
              </div>

              {/* <div className="col-12">
                <div className="form-check">
                  <input id="withLegend" className="form-check-input" type="checkbox" checked={withLegend} onChange={(e) => setWithLegend(e.target.checked)} />
                  <label className="form-check-label" htmlFor="withLegend">Deseja incluir legenda?</label>
                </div>
              </div>*/}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>

        <Button severity='secondary' raised icon="pi pi-times" style={{ borderRadius: "10px", display: "flex", gap: "10px" }} onClick={() => setShow(false)}>
          {t('common.close') || 'Fechar'}
        </Button>
        <Button severity='info' raised icon="pi pi-print" style={{ borderRadius: "10px", display: "flex", gap: "10px" }} onClick={handlePrint}>
          {t('print.print') || 'Imprimir'}
        </Button>

      </Modal.Footer>
    </Modal>
  );
}
